import React, { useState, useEffect, useRef } from 'react';
import {
  User, Tag, Eye, Edit, Trash2, ExternalLink, MapPin, Plus, X, Info, Calendar, Hash, FileText
} from 'lucide-react';
import { statusColors } from '../utils/invoiceConstants';
import { formatCurrency, formatDate } from '../services/invoiceHelpers';
import authService from '../../../services/authService';

// Get initials from a name string (same pattern as AppointmentTimeline)
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Extract job category from QB class field
const getJobCategory = (classField) => {
  if (!classField) return null;
  if (classField.includes('>')) return classField.split('>').pop().trim();
  return classField.split(':')[0];
};

/** Small (i) circle that toggles inline info expansion */
const InvoiceInfoButton = ({ invoice }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
          open ? 'bg-gray-200 text-gray-700' : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
        }`}
      >
        <Info size={14} />
      </button>
      {open && (
        <div
          className="mt-2 absolute right-0 bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600 space-y-1.5 min-w-[200px] z-10 shadow-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2">
            <Calendar size={11} className="text-gray-400 flex-shrink-0" />
            <span>Invoice: {formatDate(invoice.invoice_date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar size={11} className="text-gray-400 flex-shrink-0" />
            <span>Due: {formatDate(invoice.due_date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Hash size={11} className="text-gray-400 flex-shrink-0" />
            <span>QB: {invoice.qb_doc_number || invoice.qb_invoice_id || 'Awaiting Sync'}</span>
          </div>
          {invoice.invoice_number && (
            <div className="flex items-center gap-2">
              <Hash size={11} className="text-gray-400 flex-shrink-0" />
              <span>ID: {invoice.invoice_number}</span>
            </div>
          )}
          {invoice.created_at && (
            <div className="flex items-center gap-2">
              <Calendar size={11} className="text-gray-400 flex-shrink-0" />
              <span>Created: {formatDate(invoice.created_at)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Small (+) circle context menu for invoice card actions */
const InvoiceCardMenu = ({ invoice, onViewPDF, onEditInvoice, onDeleteInvoice, onViewCustomer, onViewJob, missingJobIds, onAddJobFromInvoice, isAdmin }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuHeight = 220;
      const spaceAbove = rect.top;
      const openUp = spaceAbove > menuHeight;
      setMenuStyle({
        position: 'fixed',
        right: window.innerWidth - rect.right,
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
        zIndex: 9999,
      });
    }
    setOpen(!open);
  };

  const hasLinkedJob = invoice.job_id && onViewJob && !missingJobIds?.has(invoice.job_id);

  const actions = [
    { label: 'View PDF', icon: Eye, onClick: () => onViewPDF(invoice) },
    { label: 'Edit Invoice', icon: Edit, onClick: () => onEditInvoice(invoice) },
    ...(onViewCustomer && (invoice.customer_ref || invoice.customer_id)
      ? [{ label: 'View Customer', icon: ExternalLink, onClick: () => onViewCustomer(invoice) }]
      : []),
    ...(hasLinkedJob
      ? [{ label: 'View Job', icon: FileText, onClick: () => onViewJob(invoice) }]
      : []),
    ...(!hasLinkedJob && onAddJobFromInvoice
      ? [{ label: 'Add Job', icon: Plus, onClick: () => onAddJobFromInvoice(invoice) }]
      : []),
    ...(isAdmin
      ? [{ label: 'Delete', icon: Trash2, onClick: () => onDeleteInvoice(invoice), danger: true }]
      : []),
  ];

  return (
    <div ref={ref} className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        className="w-7 h-7 rounded-full bg-pest-blue text-white flex items-center justify-center hover:bg-blue-600 transition-colors shadow-sm"
      >
        {open ? <X size={14} /> : <Plus size={14} />}
      </button>
      {open && (
        <div style={menuStyle} className="bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[170px]">
          {actions.map(action => (
            <button
              key={action.label}
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen(false); action.onClick(); }}
              className={`flex items-center gap-2.5 w-full px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${action.danger ? 'text-red-600' : 'text-gray-700'}`}
            >
              <action.icon size={14} className={action.danger ? 'text-red-500' : 'text-pest-blue'} />
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const InvoiceTable = ({ invoices, onViewInvoice, onViewPDF, onDeleteInvoice, onEditInvoice, onViewCustomer, onViewJob, missingJobIds, onAddJobFromInvoice, userProfile }) => {

  // Build treatment address string from invoice fields
  const getTreatmentAddress = (invoice) => {
    const parts = [
      invoice.ship_addr_line1,
      invoice.ship_addr_city,
      invoice.ship_addr_postal_code
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const isAdmin = userProfile?.role === 'admin' || authService.hasAdminReturn();

  const renderMobileCard = (invoice) => {
    const treatmentAddr = getTreatmentAddress(invoice);
    const jobCategory = getJobCategory(invoice.class);
    return (
      <div
        key={invoice.id}
        onClick={() => onViewInvoice(invoice)}
        className="bg-white rounded-xl border border-pest-blue/20 shadow-sm px-3 py-3 cursor-pointer active:bg-gray-50"
      >
        {/* Row 1: Status pill left, total right */}
        <div className="flex items-center justify-between mb-1.5">
          <span className={`px-2.5 py-1 text-sm font-bold rounded-md capitalize ${statusColors[invoice.status]}`}>
            {invoice.status}
          </span>
          <span className="text-lg font-bold text-gray-900">
            {formatCurrency(invoice.total_amount)}
          </span>
        </div>

        {/* Invoice date */}
        <div className="text-xs text-gray-500 mt-0.5">
          <Calendar size={11} className="inline mr-1 text-gray-400" />
          {formatDate(invoice.invoice_date)}
        </div>

        {/* Customer name */}
        <div className="text-sm font-semibold text-gray-900 truncate mt-1">
          {invoice.customer_name}
        </div>

        {/* Treatment address */}
        {treatmentAddr && (
          <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
            <MapPin size={12} className="flex-shrink-0 text-gray-400" />
            <span className="truncate">{treatmentAddr}</span>
          </div>
        )}

        {/* Technician */}
        {invoice.technician_name && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <div className="w-5 h-5 rounded-full bg-pest-blue text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
              {getInitials(invoice.technician_name)}
            </div>
            <span className="text-sm text-gray-600 truncate">{invoice.technician_name}</span>
          </div>
        )}

        {/* Job category */}
        {jobCategory && (
          <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
            <Tag size={12} className="flex-shrink-0 text-gray-400" />
            <span className="truncate">{jobCategory}</span>
          </div>
        )}

        {/* Bottom row: balance left, info + actions right */}
        <div className="flex items-center justify-between mt-2.5">
          <div>
            {invoice.balance_due > 0 && (
              <span className="text-sm font-semibold text-red-600">
                Bal: {formatCurrency(invoice.balance_due)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <InvoiceInfoButton invoice={invoice} />
            <InvoiceCardMenu
              invoice={invoice}
              onViewPDF={onViewPDF}
              onEditInvoice={onEditInvoice}
              onDeleteInvoice={onDeleteInvoice}
              onViewCustomer={onViewCustomer}
              onViewJob={onViewJob}
              missingJobIds={missingJobIds}
              onAddJobFromInvoice={onAddJobFromInvoice}
              isAdmin={isAdmin}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Desktop: existing table — completely unchanged */}
      <div className="hidden md:block">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-[10%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice
                </th>
                <th className="w-[16%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="w-[12%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Technician
                </th>
                <th className="w-[17%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Job
                </th>
                <th className="w-[9%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice Date
                </th>
                <th className="w-[8%] px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="w-[8%] px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Balance
                </th>
                <th className="w-[8%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="w-[12%] px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.map((invoice) => {
                const treatmentAddr = getTreatmentAddress(invoice);
                return (
                  <tr
                    key={invoice.id}
                    onClick={() => onViewInvoice(invoice)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-2 py-3">
                      <div className="text-xs font-mono text-gray-400">
                        {invoice.invoice_number && <>ID: {invoice.invoice_number}</>}
                        {invoice.invoice_number && ' '}
                        QB: {invoice.qb_doc_number || invoice.qb_invoice_id || 'Awaiting Sync'}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Due: {formatDate(invoice.due_date)}
                      </div>
                    </td>
                    <td className="px-2 py-3">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {invoice.customer_name}
                      </div>
                      {invoice.customer_email && (
                        <div className="text-xs text-gray-500 truncate">
                          {invoice.customer_email}
                        </div>
                      )}
                      {(invoice.customer_ref || invoice.customer_id) && onViewCustomer && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewCustomer(invoice);
                          }}
                          className="inline-flex items-center gap-1 text-xs text-pest-blue hover:text-pest-blue-dark transition-colors duration-200 mt-0.5 hover:underline"
                        >
                          Customer Details
                          <ExternalLink size={10} />
                        </button>
                      )}
                    </td>
                    <td className="px-2 py-3">
                      <div className="flex items-center text-sm text-gray-900">
                        <User size={13} className="mr-1 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{invoice.technician_name || '-'}</span>
                      </div>
                      {userProfile?.role === 'admin' && invoice.franchisee_company && (
                        <div className="text-xs text-gray-500 ml-4 truncate">
                          {invoice.franchisee_company}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-3">
                      {invoice.class ? (
                        <div>
                          <div className="flex items-center text-sm text-gray-900">
                            <Tag size={13} className="mr-1 text-gray-400 flex-shrink-0" />
                            <span className="truncate font-medium">{invoice.class.includes('>') ? invoice.class.split('>').pop().trim() : invoice.class.split(':')[0]}</span>
                          </div>
                          {treatmentAddr && (
                            <div className="flex items-center text-xs text-gray-500 mt-0.5">
                              <MapPin size={11} className="mr-1 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{treatmentAddr}</span>
                            </div>
                          )}
                          {invoice.job_id && onViewJob && !missingJobIds?.has(invoice.job_id) ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewJob(invoice);
                              }}
                              className="inline-flex items-center gap-1 text-xs text-pest-blue hover:text-pest-blue-dark transition-colors duration-200 mt-0.5 hover:underline"
                            >
                              Job Details
                              <ExternalLink size={10} />
                            </button>
                          ) : (
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-red-500 font-medium">No Linked Job</span>
                              {onAddJobFromInvoice && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); onAddJobFromInvoice(invoice); }}
                                  className="inline-flex items-center gap-0.5 text-xs text-pest-blue hover:text-pest-blue-dark hover:underline"
                                >
                                  <Plus size={10} />Add Job
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <span className="text-xs text-red-500 font-medium">No Linked Job</span>
                          {onAddJobFromInvoice && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onAddJobFromInvoice(invoice); }}
                              className="inline-flex items-center gap-0.5 text-xs text-pest-blue hover:text-pest-blue-dark hover:underline ml-2"
                            >
                              <Plus size={10} />Add Job
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-3">
                      <div className="text-sm text-gray-900 whitespace-nowrap">
                        {formatDate(invoice.invoice_date)}
                      </div>
                    </td>
                    <td className="px-2 py-3 text-right">
                      <div className="text-sm font-medium text-gray-900 whitespace-nowrap">
                        {formatCurrency(invoice.total_amount)}
                      </div>
                    </td>
                    <td className="px-2 py-3 text-right">
                      <div className={`text-sm font-medium whitespace-nowrap ${
                        invoice.balance_due > 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {formatCurrency(invoice.balance_due)}
                      </div>
                    </td>
                    <td className="px-2 py-3">
                      <span className={`px-2.5 py-1 text-sm font-bold rounded-md capitalize ${statusColors[invoice.status]}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-2 py-3">
                      <div className="flex justify-end space-x-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewPDF(invoice);
                          }}
                          className="btn btn-sm btn-secondary"
                          title={`View PDF for ${invoice.invoice_number}`}
                        >
                          <Eye size={12} />
                          <span>PDF</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditInvoice(invoice);
                          }}
                          className="btn btn-sm btn-secondary"
                          title={`Edit invoice ${invoice.invoice_number}`}
                        >
                          <Edit size={12} />
                          <span>Edit</span>
                        </button>
                        {(userProfile?.role === 'admin' || authService.hasAdminReturn()) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteInvoice(invoice);
                            }}
                            className="btn btn-sm btn-danger"
                            title={`Delete invoice ${invoice.invoice_number}`}
                          >
                            <Trash2 size={12} />
                            <span>Delete</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile: card list */}
      <div className="md:hidden space-y-2 px-2">
        {invoices.map(renderMobileCard)}
      </div>
    </>
  );
};

export default InvoiceTable;
